import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Badge, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faHome,
  faChartBar,
  faArrowLeft,
  faSearch
} from '@fortawesome/free-solid-svg-icons';

/**
 * NotFound (404) Component
 * Modernized responsive 404 view displaying the missing URL and helpful navigation options.
 */
const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const requestedPath = location.pathname || (typeof window !== 'undefined' ? window.location.pathname : '');

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = "404 - Page Not Found | HealthDash";
    }
  }, []);

  return (
    <Container className="py-5 mt-4">
      <Row className="justify-content-center">
        <Col xs={12} md={10} lg={8}>
          <Card className="shadow-sm border-0 rounded-3 overflow-hidden text-center p-4" data-testid="notfound-container">
            <Card.Header className="bg-white border-bottom-0 pt-3 pb-2">
              <div className="text-warning mb-3 display-4">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <Badge bg="warning" text="dark" className="text-uppercase px-3 py-1 mb-2">
                404 — Page Not Found
              </Badge>
              <h2 className="fw-bold my-2 text-dark">Page Not Found</h2>
              <p className="text-muted small mb-0">
                Cloud Dashboard (CD) &amp; Personalized Cloud Dashboard (PCD)
              </p>
            </Card.Header>

            <Card.Body className="px-4 pt-2 pb-3">
              <Alert variant="warning" className="border-0 shadow-sm text-start mb-4" role="alert">
                <div className="d-flex align-items-start">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 mt-1 flex-shrink-0 text-warning" />
                  <div>
                    <strong>Missing Resource:</strong> We could not find the page at{' '}
                    <code className="text-dark bg-white px-2 py-1 rounded border font-monospace" data-testid="missing-path">
                      {requestedPath || 'the requested URL'}
                    </code>
                    .
                    <div className="mt-1 small text-muted">
                      The URL might be misspelled, or the page may have been moved or removed from the platform.
                    </div>
                  </div>
                </div>
              </Alert>

              <div className="text-start bg-white border rounded-3 p-3 mb-4">
                <h6 className="fw-bold text-dark mb-2">Helpful next steps:</h6>
                <ul className="text-muted small mb-0 ps-3">
                  <li className="mb-1">Verify that the URL in your browser address bar is spelled correctly.</li>
                  <li className="mb-1">Return to the Beer Bank or your Personalized Cloud Dashboard (PCD) to continue browsing.</li>
                  <li>
                    If you are diagnosing health data synchronization errors, check the{' '}
                    <Link to="/ecosystem/SER" className="text-decoration-underline text-warning fw-bold">
                      Synchronization Error Reporting (SER)
                    </Link>{' '}
                    knowledge base.
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="d-flex flex-wrap justify-content-center gap-2 pt-2">
                <Link to="/" className="btn btn-warning shadow-sm px-4 py-2 fw-bold text-dark text-uppercase">
                  <FontAwesomeIcon icon={faHome} className="me-2" />
                  Return Home
                </Link>
                <Link to="/user" className="btn btn-outline-dark shadow-sm px-4 py-2 fw-bold">
                  <FontAwesomeIcon icon={faChartBar} className="me-2" />
                  Dashboard
                </Link>
                <Button
                  variant="outline-secondary"
                  className="shadow-sm px-4 py-2"
                  onClick={() => navigate(-1)}
                  data-testid="notfound-back-button"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                  Go Back
                </Button>
                <Link to="/ecosystem/SER" className="btn btn-outline-warning shadow-sm px-4 py-2 text-dark">
                  <FontAwesomeIcon icon={faSearch} className="me-2" />
                  Knowledge Base (SER)
                </Link>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default NotFound;